import type {
  DailyStartLineAllTime,
  DailyStartLineBoard,
  DailyStartLineEnd,
  DailyStartLineSubmitInput,
} from '../../firebase';
import type { RunPutt } from '../landscape/StartLineStreakLandscape';
import type { TvSummary, TvSummaryStartLinesSection } from '../../tv/summary';
import {
  averageAbsAngleDeg,
  formatAverageAngle,
  runBoardRows,
  runCardTvLayout,
  type TvGameOverColors,
} from '../../tv/gameOverSections';

/**
 * Pure rules for the Daily Start Line screen (DAILY_START_LINE.md). The server
 * (functions/src/lib/dailyStartLine.ts) scores and ranks; these only shape
 * what the app sends and shows.
 */

export interface DailyStartLineRoundIdentity {
  ownerUid: string | null;
  profileId: string;
  profileName: string;
  isPrimaryProfile: boolean;
  onBehalfOfUid: string | null;
}

export interface DailyStartLineClaimPin {
  claimId: string;
  dateKey: string;
  distanceFt: number;
}

/**
 * Today's number to beat, locked when the round opens. Null when nobody has
 * played today (no race at all, the Weekly Open rule of 2026-09-30), and when
 * the only row is the player's own (they cannot play again anyway).
 */
export function dailyStartLineRace(
  leader: DailyStartLineBoard['leader'] | null | undefined,
  nameFor: (row: { handle: string; anonymous?: boolean }) => string
): { label: string; streak: number } | null {
  if (!leader || leader.isYou || !Number.isFinite(leader.streak) || leader.streak < 1) return null;
  return { label: nameFor(leader), streak: leader.streak };
}

/** The putts judged into the run, as the server scores them: from the first
 *  make on (warm-up misses never enter the run). */
export function dailyStartLineSubmitInput(args: {
  identity: DailyStartLineRoundIdentity;
  claim: DailyStartLineClaimPin;
  run: readonly RunPutt[];
  endReason: DailyStartLineEnd;
  mechanical: boolean;
}): DailyStartLineSubmitInput {
  const { identity, claim, run, endReason, mechanical } = args;
  return {
    mode: 'startline',
    profileId: identity.profileId,
    profileName: identity.profileName,
    isPrimaryProfile: identity.isPrimaryProfile,
    onBehalfOfUid: identity.onBehalfOfUid ?? '',
    expectedCallerUid: identity.ownerUid ?? '',
    attemptClaimId: claim.claimId,
    attemptDateKey: claim.dateKey,
    attemptDistanceFt: claim.distanceFt,
    putts: run.map((putt) => ({
      holed: putt.made,
      angleDeg: putt.angleDeg !== null && Number.isFinite(putt.angleDeg) ? putt.angleDeg : null,
      distanceFt: putt.distanceFt,
      speedMps: putt.speedMps,
      simulated: putt.simulated,
    })),
    endReason,
    mechanical,
  };
}

/** "#3 of 40", crowned at #1. The headline is where THIS attempt places, not
 *  the player's older best (the Daily Distance fix of 2026-09-30). A run that
 *  is not a new best is not on the board, so it places among everyone's bests
 *  plus itself: one more than the board count ("#2 of 1", Cole 2026-09-30). */
export function dailyStartLineAllTimeHeadline(allTime: DailyStartLineAllTime): string {
  const place = allTime.isPersonalBest ? allTime.rank : allTime.roundRank ?? allTime.rank;
  const field = allTime.isPersonalBest || allTime.roundRank === null ? allTime.totalCount : allTime.totalCount + 1;
  return `${place === 1 ? '\u{1F451} ' : ''}#${place ?? '-'} of ${Math.max(field, place ?? 0)}`;
}

export function dailyStartLineAllTimeSub(allTime: DailyStartLineAllTime): string {
  if (allTime.isPersonalBest) return 'New personal best';
  if (allTime.bestStreak !== null && allTime.rank !== null) {
    return `Your best: #${allTime.rank}, ${allTime.bestStreak} in a row`;
  }
  return allTime.bestStreak !== null ? `Your best: ${allTime.bestStreak} in a row` : 'Your best stands';
}

export function dailyStartLineRankLabel(rank: number | null, totalCount: number): string {
  if (rank === null) return 'No rank yet';
  return `${rank === 1 ? '\u{1F451} ' : ''}#${rank} of ${totalCount}`;
}

export function dailyStartLineEndLabel(endReason: DailyStartLineEnd): string {
  return endReason === 'left' ? 'IN A ROW · ENDED EARLY' : 'IN A ROW';
}

/**
 * The Daily Start Line result on the TV (summary sweep, 2026-09-30): the
 * streak, the distance, today's rank, the all-time placement, the average
 * start line, today's board, and the run's start-line chart.
 */
export function dailyStartLineTvSummary(input: {
  result: { streak: number; distanceFt: number; endReason: DailyStartLineEnd; status: 'submitting' | 'done' | 'error'; angles?: Array<number | null> };
  /** "#3 of 40"; null when the attempt is not ranked. */
  dayRank: string | null;
  allTime: { headline: string; sub: string } | null;
  board: Array<{ rank: number; name: string; value: string; you: boolean }> | null;
  colors: TvGameOverColors;
  palette: TvSummaryStartLinesSection['palette'];
}): TvSummary {
  const { result, colors } = input;
  const angles = result.angles ?? [];
  const avg = averageAbsAngleDeg(angles);
  const status =
    result.status === 'submitting' ? 'Submitting today’s streak' : result.status === 'error' ? 'Not submitted' : null;
  return {
    kicker: `DAILY · ${result.distanceFt} FT`,
    title: String(result.streak),
    headline: dailyStartLineEndLabel(result.endReason),
    rows: [
      ...(input.dayRank ? [{ label: 'TODAY', value: input.dayRank, tone: 'gold' as const }] : []),
      ...(input.allTime ? [{ label: 'ALL-TIME', value: input.allTime.headline }] : []),
    ],
    ...runCardTvLayout({
      eyebrow: `DAILY START LINE · ${result.distanceFt} FT`,
      value: String(result.streak),
      valueLabel: dailyStartLineEndLabel(result.endReason),
      detail: status,
      detailColor: colors.textSecondary,
      tiles: [
        { label: 'DISTANCE', value: `${result.distanceFt} FT` },
        { label: 'AVG START LINE', value: formatAverageAngle(avg), sub: avg === null ? null : 'Off center, either side' },
        { label: 'TODAY’S RANK', value: input.dayRank ?? (result.status === 'done' ? 'Not ranked' : '--') },
        {
          label: 'ALL-TIME',
          value: input.allTime?.headline ?? '--',
          sub: input.allTime?.sub ?? null,
        },
      ],
      startLines: angles.some((angle) => angle !== null)
        ? { title: 'START LINES · THIS RUN', angles, palette: input.palette }
        : null,
      board: input.board?.length ? { title: 'TODAY’S LEADERBOARD', rows: runBoardRows(input.board, colors) } : null,
      colors,
    }),
  };
}
